"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle } from "lucide-react";
import Image from "next/image";
import { useEffect, useState, useSyncExternalStore } from "react";
import { FormattedTime } from "@defuse/ui/formatted-time";
import { KNOWN_CHAINS } from "@intents-explorer/shared-api/chains";
import { earliest, getScopeTypeLabel, isChainScopeType, isTokenScopeType, type ScopeGroup, scopeKey } from "@intents-explorer/shared-api/shield-scope";
import { getTokenIcon, getTokenLabel } from "@intents-explorer/shared-api/tokens";
import { numToStr } from "@intents-explorer/shared-api/type-safe-stringify";
import { ChainIcon } from "@defuse/ui/chain-icon";
import { useTokens } from "@defuse/ui/tokens-provider";
import { Short } from "@/components/short";
import type { ActiveIncident } from "@/lib/systemStatus";
export function SystemStatusBannerClient(t0) {
  const $ = _c(12);
  const {
    groups
  } = t0;
  const [index, setIndex] = useState(0);
  let t1;
  let t2;
  if ($[0] !== groups.length) {
    t1 = () => {
      if (groups.length <= 1) {
        return;
      }
      const interval = setInterval(() => {
        setIndex(currentIndex => (currentIndex + 1) % groups.length);
      }, 6000);
      return () => {
        clearInterval(interval);
      };
    };
    t2 = [groups.length];
    $[0] = groups.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const position = index % groups.length;
  const group = groups[position];
  if (group === undefined) {
    return null;
  }
  let t3;
  if ($[3] !== group) {
    t3 = scopeKey(group);
    $[3] = group;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== groups.length || $[6] !== position) {
    t4 = groups.length > 1 ? `Ongoing Incident ${numToStr(position + 1)} of ${numToStr(groups.length)}:` : "Ongoing Incident:";
    $[5] = groups.length;
    $[6] = position;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== group || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="w-full"><BannerItem key={t3} group={group} label={t4} /></div>;
    $[8] = group;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * False while rendering on the server and for the first client render. The
 * timestamp depends on the reader's time zone, so it stays out until then
 * rather than flashing a placeholder sized for a UTC date.
 */
function useHydrated() {
  return useSyncExternalStore(_temp2, _temp3, _temp4);
}
function _temp4() {
  return false;
}
function _temp3() {
  return true;
}
function _temp2() {
  return _temp;
}
function _temp() {}
function IncidentScope(t0) {
  const $ = _c(32);
  const {
    scopeType,
    scopeValue
  } = t0;
  const tokens = useTokens();
  const isChainScope = isChainScopeType(scopeType);
  if (isChainScope && scopeValue === "*") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span>all chains</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const chain = isChainScope ? KNOWN_CHAINS[scopeValue] : undefined;
  if (chain !== undefined) {
    let t1;
    if ($[1] !== scopeValue) {
      t1 = <ChainIcon chain={scopeValue} />;
      $[1] = scopeValue;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== chain.label || $[4] !== t1) {
      t2 = <span className="inline-flex items-center gap-1.5">{t1}{chain.label}</span>;
      $[3] = chain.label;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== scopeType || $[7] !== scopeValue || $[8] !== tokens) {
    t1 = isTokenScopeType(scopeType) ? getTokenLabel(tokens, scopeValue) : undefined;
    $[6] = scopeType;
    $[7] = scopeValue;
    $[8] = tokens;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const label = t1;
  if (label !== undefined) {
    let t2;
    if ($[10] !== scopeValue || $[11] !== tokens) {
      t2 = getTokenIcon(tokens, scopeValue);
      $[10] = scopeValue;
      $[11] = tokens;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t2) {
      t3 = <Image src={t2} alt="" width={16} height={16} className="h-4 w-4" />;
      $[13] = t2;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== scopeType) {
      t4 = getScopeTypeLabel(scopeType);
      $[15] = scopeType;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t4) {
      t5 = <span className="opacity-70">{t4}</span>;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== label || $[20] !== t3 || $[21] !== t5) {
      t6 = <span className="inline-flex items-center gap-1.5">{t3}{label}{t5}</span>;
      $[19] = label;
      $[20] = t3;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  let t2;
  if ($[23] !== scopeValue) {
    t2 = <Short text={scopeValue} length={21} showCopy={false} />;
    $[23] = scopeValue;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  let t3;
  if ($[25] !== scopeType) {
    t3 = getScopeTypeLabel(scopeType);
    $[25] = scopeType;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  let t4;
  if ($[27] !== t3) {
    t4 = <span className="opacity-70">{t3}</span>;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== t2 || $[30] !== t4) {
    t5 = <span className="inline-flex items-center gap-1.5">{t2}{t4}</span>;
    $[29] = t2;
    $[30] = t4;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  return t5;
}
function BannerItem(t0) {
  const $ = _c(16);
  const {
    group,
    label
  } = t0;
  const hydrated = useHydrated();
  let t1;
  if ($[0] !== group.incidents) {
    t1 = earliest(group.incidents);
    $[0] = group.incidents;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const incident = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AlertCircle className="h-4 w-4 shrink-0 text-current" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== incident) {
    t3 = <IncidentScope {...incident} />;
    $[3] = incident;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== group.incidents || $[6] !== hydrated || $[7] !== incident) {
    t4 = hydrated && <span className="hidden items-center gap-1.5 sm:inline-flex">{group.incidents.length > 1 ? "first opened" : "opened"}<FormattedTime date={incident.createdAt} showRelativeTime={true} /></span>;
    $[5] = group.incidents;
    $[6] = hydrated;
    $[7] = incident;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== label || $[10] !== t3 || $[11] !== t4) {
    t5 = <p className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-xs font-medium whitespace-nowrap">{label}{t3}{t4}</p>;
    $[9] = label;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <a href="https://partners.near-intents.org/shield/status" target="_blank" rel="noreferrer noopener" className="shrink-0 text-xs font-semibold underline underline-offset-2">View status</a>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5) {
    t7 = <div className="h-9 border-b border-yellow-300 bg-yellow-100 text-yellow-950 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-100"><div className="mx-auto flex h-full w-full max-w-3xl items-center gap-2 px-4 xl:max-w-5xl">{t2}{t5}{t6}</div></div>;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}